import type { ActionTree, MutationTree, GetterTree } from 'vuex'
import RootState from '@web/store/types'
import ArticleAPI from '@club/api/modules/article'
import type { FeedState, RawFeed, Feed } from '@club/types/Feed'
import type { Tag } from '@club/types/Tag'
import type { Article } from '@club/types/Article'
import type { Pagination } from '@club/types/Pagination'
import { tagType } from '@club/store/tag'
import { isDefined } from 'ts-is-present'
import type { Community } from '@club/types/Community'
import Logger from '@web/common/Logger'

enum FEED_TYPE {
  all = 'all',
  favourite = 'favourite',
  category = 'category',
  tag = 'tag'
}

const state: FeedState = {
  feeds: new Map(),
  feedLoading: false
}

const articleAPI = new ArticleAPI()

function getFeedId ({ tags, community }: { tags: Tag[], community: Community }): string {
  return `${community?.id || 0}-` + tags
    .filter(tag => tag.isSelected)
    .map(tag => tag.id)
    .join('-')
}

function getFeed ({ feeds, feedId, getArticleById }: { feeds: Map<string, RawFeed>, feedId: string, getArticleById: (id: number) => Article | undefined }): Feed | undefined {
  if (!feeds.has(feedId)) {
    return undefined
  }
  const feed = feeds.get(feedId)
  if (!feed) {
    return undefined
  }
  const articles: Article[] = Array.from(feed.articleIds)
    .map(getArticleById)
    .filter(isDefined)
  return {
    articles,
    pagination: feed.pagination
  }
}

function getFeedIsOver (feed: RawFeed | undefined): boolean {
  if (!feed) {
    return false
  }
  return !feed.pagination.next
}

const getters: GetterTree<FeedState, RootState> = {
  feedId: (state, getters, rootState, rootGetters): string => getFeedId({
    tags: rootGetters['clubTag/tags'],
    community: rootGetters['clubCommunity/selectedCommunity']
  }),
  getRawFeedById: ({ feeds }) => (feedId: string): RawFeed | undefined => feeds.get(feedId),
  feed: ({ feeds }, getters, rootState, rootGetters): Feed | undefined => {
    const feedId: string = getters.feedId

    const getArticleById: (id: number) => Article | undefined = rootGetters['clubArticles/articleById']
    return getFeed({ feeds, feedId, getArticleById })
  },
  feedByTag: ({ feeds }, getters, rootState, rootGetters) => (feedId: string): Feed | undefined => {
    const getArticleById: (id: number) => Article | undefined = rootGetters['clubArticles/articleById']
    return getFeed({ feeds, feedId, getArticleById })
  },

  feedType (state, getters, rootState, rootGetters) {
    const tags: Tag[] = rootGetters['clubTag/tags'].filter((tag: Tag) => tag.isSelected)

    const isAll: boolean = tags.every(tag => tag.type === tagType.reset)
    if (isAll) {
      return {
        type: FEED_TYPE.all
      }
    }

    const isFavourite: boolean = tags.every(tag => tag.type === tagType.favourite)
    if (isFavourite) {
      return {
        type: FEED_TYPE.favourite
      }
    }

    const isCategory: boolean = tags.every(tag => tag.type === tagType.category)
    if (isCategory) {
      return {
        type: FEED_TYPE.category,
        params: {
          categoryIds: tags.map(tag => tag.id)
        }
      }
    }

    Logger.warn('Неизвестный тип ленты')
    Logger.info('Tags: ', Array.from(tags))
    return {
      type: FEED_TYPE.all
    }
  },

  feedLoading: (state: FeedState) => state.feedLoading,

  feedIsOver: (state, getters): boolean => {
    const feedId: string = getters.feedId
    const feed: RawFeed | undefined = state.feeds.get(feedId)
    return getFeedIsOver(feed)
  },
  feedIsOverByTag: (state) => (feedId: string): boolean => {
    const feed: RawFeed | undefined = state.feeds.get(feedId)
    return getFeedIsOver(feed)
  }
}

const mutations: MutationTree<FeedState> = {
  receiveFeed (state: FeedState, { feedId, feed }: { feedId: string; feed: RawFeed }) {
    state.feeds.set(feedId, feed)
  },
  updateFeed (state: FeedState, { feedId, feed }: { feedId: string; feed: RawFeed }) {
    const { articleIds } = state.feeds.get(feedId) as RawFeed

    state.feeds.set(feedId, {
      pagination: feed.pagination,
      articleIds: new Set<number>([...Array.from<number>(articleIds), ...Array.from(feed.articleIds)])
    })
  },
  updLoadingStatus (state: FeedState, status: boolean) {
    state.feedLoading = status
  },
  logout (state: FeedState) {
    state.feeds = new Map()
  }
}

const actions: ActionTree<FeedState, RootState> = {
  async fetchFeed ({ commit, getters, rootGetters }): Promise<void> {
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']

    let requestFeed: Promise<{ items: Article[], pagination: Pagination }>
    const feed: RawFeed | undefined = getters.getRawFeedById(getters.feedId)
    const feedType = getters.feedType

    const params: {
      categoryIds?: number[];
      communityIds?: number[];
      communityExternalIds?: string[];
      next?: string;
      width?: number;
      height?: number;
    } = {}

    if (feed) {
      // если статей меньше, чем обещает АПИ, значит запрашиваешь следующую страницу
      if (feed.pagination.next) {
        params.next = feed.pagination.next
      }
    }

    if (selectedCommunity) {
      params.communityExternalIds = [selectedCommunity.uniq]
    }

    params.width = 700
    params.height = 400

    switch (feedType.type) {
      case FEED_TYPE.category:
        params.categoryIds = feedType.params.categoryIds
        requestFeed = articleAPI.fetchAll(params)
        break
      case FEED_TYPE.all:
        requestFeed = articleAPI.fetchAll(params)
        break
      case FEED_TYPE.favourite:
        requestFeed = articleAPI.fetchFavorite(params)
        break
      default:
        requestFeed = articleAPI.fetchAll(params)
        Logger.warn(`Request feed with type "${feedType.type}" not implemented`)
    }

    commit('updLoadingStatus', true)
    const { items: articles, pagination } = await requestFeed.finally(() => { commit('updLoadingStatus', false) })
    const articleIds: Set<number> = new Set([...articles.map(a => a.id)])
    if (feed) {
      commit('updateFeed', {
        feedId: getters.feedId,
        feed: {
          articleIds,
          pagination
        }
      })
    } else {
      commit('receiveFeed', {
        feedId: getters.feedId,
        feed: {
          articleIds,
          pagination
        }
      })
    }
    articles.forEach((article) => {
      commit('clubArticles/addArticle', article, { root: true })
    })
  },
  async fetchFeedByTags ({ state, commit, rootGetters }, { tagId, order, next }: { tagId: string, order: string, next?: boolean }): Promise<void> {
    const feedId = `tag_${tagId}_${order}`

    const feed: RawFeed | undefined = state.feeds.get(feedId)

    const params: {
      tagIds: string[];
      communityExternalIds?: string[];
      order?: Record<string, 'asc' | 'desc'>;
      next?: string;
      width?: number;
      height?: number;
    } = {
      tagIds: [tagId],
      order: {
        [order]: 'desc'
      }
    }

    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (selectedCommunity) {
      params.communityExternalIds = [selectedCommunity.uniq]
    }

    if (feed && next) {
      if (feed.pagination.next) {
        params.next = feed.pagination.next
      }
    }

    params.width = 700
    params.height = 400

    const requestFeed: Promise<{ items: Article[], pagination: Pagination }> = articleAPI.fetchAll(params)

    commit('updLoadingStatus', true)
    const { items: articles, pagination } = await requestFeed.finally(() => { commit('updLoadingStatus', false) })
    const articleIds: Set<number> = new Set([...articles.map(a => a.id)])
    if (feed) {
      commit('updateFeed', {
        feedId,
        feed: {
          articleIds,
          pagination
        }
      })
    } else {
      commit('receiveFeed', {
        feedId,
        feed: {
          articleIds,
          pagination
        }
      })
    }
    articles.forEach((article) => {
      commit('clubArticles/addArticle', article, { root: true })
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
